import { useCreate, Loading, useNotify, useDataProvider, useGetIdentity } from 'react-admin';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Divider,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';

import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { useEffect, useState } from 'react';

const taskConfig = [
  { step: 'MOVE', title: '1. Move', desc: 'Copy Development files to Y:/Billing Reports.' },
  { step: 'BACKUP', title: '2. Create Backup', desc: 'Archive final files to dated backup folder.' },
  { step: 'SYNC', title: '3. Sync to FTP', desc: 'Distribute reports to client FTP servers.' },
  { step: 'CLEANUP', title: '4. Cleanup', desc: 'Clear the Billing Reports folder for the next cycle.' },
];

const calculateFolderFromParts = (week: number, month: number, year: number) => {
  const weekStartDays = [1, 9, 16, 24];
  const startDay = weekStartDays[week - 1];

  let endDay;
  if (week < 4) {
    endDay = weekStartDays[week] - 1;
  } else {
    endDay = new Date(year, month + 1, 0).getDate();
  }

  const format = (day: number) => {
    const d = new Date(year, month, day);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}${dd}${yy}`;
  };

  return `Billing_Archive_${format(startDay)}-${format(endDay)}`;
};

export const BillingOperations = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [execute, { isLoading: isTriggering }] = useCreate();
  const { identity } = useGetIdentity();

  const [job, setJob] = useState<any>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const [customFolderName, setCustomFolderName] = useState('');

  const [hasConfigured, setHasConfigured] = useState(false);

  const [selWeek, setSelWeek] = useState(1);
  const [selMonth, setSelMonth] = useState(new Date().getMonth());
  const [selYear, setSelYear] = useState(new Date().getFullYear());

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingParams, setPendingParams] = useState<any>(null);

  const [ftpConfirmOpen, setFtpConfirmOpen] = useState(false);
  const [issuesDialogOpen, setIssuesDialogOpen] = useState(false);
  const [selectedStageIssues, setSelectedStageIssues] = useState<any[]>([]);
  const [loadingIssues, setLoadingIssues] = useState(false);

  const [actionConfirmOpen, setActionConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState<{ type: 'STOP' | 'CLOSE'; stageId?: number }>({ type: 'STOP' });

  const [checkingFtp, setCheckingFtp] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);

  const [submittingStep, setSubmittingStep] = useState<string | null>(null);

  const [zipDialogOpen, setZipDialogOpen] = useState(false);

  const zipAttempts = job?.stages
    ? job.stages.filter((s: any) => s.step === 'ZIP_INVOICES').sort((a: any, b: any) => b.stageId - a.stageId)
    : [];

  const isAnythingRunning = job?.stages?.some((s: any) => s.status === 'PROCESSING' || s.status === 'RUNNING');

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const years = [2024, 2025, 2026, 2027];

  const latestBackupStage = job?.stages?.find((s: any) => s.step === 'BACKUP' && s.status === 'COMPLETED');

  let savedFolderName = '';
  if (latestBackupStage?.filters) {
    try {
      const parsedFilters =
        typeof latestBackupStage.filters === 'string'
          ? JSON.parse(latestBackupStage.filters)
          : latestBackupStage.filters;

      savedFolderName = parsedFilters.backupFolderName;
    } catch (e) {}
  }

  const activeFolderName =
    customFolderName || savedFolderName || (hasConfigured ? calculateFolderFromParts(selWeek, selMonth, selYear) : '');

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const getBenchmarkNote = (step: string) => {
    switch (step) {
      case 'MOVE':
      case 'BACKUP':
        return 'Est: ~6-7 mins per 10k files';
      case 'SYNC':
        return 'Bundle: ~2 mins | Sync: ~10 mins';
      case 'CLEANUP':
        return 'Est: ~1-2 mins per 10k files';
      default:
        return null;
    }
  };

  const fetchActiveJob = async () => {
    try {
      const { data } = await dataProvider.getOne('ActiveBillingJob', { id: 'current' });
      setJob(data);
    } catch (error) {
      setJob(null);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveJob();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const isAnyTaskRunning = job?.stages?.some((s: any) => s.status === 'PROCESSING');

    if (isAnyTaskRunning) {
      interval = setInterval(fetchActiveJob, 10000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [job?.stages]);

  const handleStartClick = async (params: any) => {
    const paramsWithYear = {
      ...params,
      backupYear: selYear,
    };
    if (params.step === 'BACKUP') {
      try {
        const { data } = await dataProvider.getOne('CheckAzureFolderExists', {
          id: activeFolderName,
          meta: { backupYear: selYear.toString() },
        });

        if (data.exists) {
          setPendingParams(paramsWithYear);
          setConfirmDialogOpen(true);
          return;
        }
      } catch (error) {}
    }

    if (params.step === 'SYNC') {
      setCheckingFtp(true);
      try {
        const { data } = await dataProvider.getOne('CheckFtpFilesExist', { id: 'current' });
        setCheckingFtp(false);
        if (data.exists) {
          setPendingParams(params);
          setFtpConfirmOpen(true);
          return;
        }
      } catch (error) {
        setCheckingFtp(false);
      }
    }
    handleStartTask(params);
  };

  const handleViewIssues = async (stageId: string) => {
    setLoadingIssues(true);
    setIssuesDialogOpen(true);
    try {
      const { data } = await dataProvider.getList('GetBillingStepIssues', {
        filter: { stageId },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'file_name', order: 'ASC' },
      });
      setSelectedStageIssues(data);
    } catch (error) {
      notify('Failed to load issues', { type: 'error' });
    } finally {
      setLoadingIssues(false);
    }
  };

  const handleStartTask = (params: any) => {
    setSubmittingStep(params.step);
    execute(
      'ExecuteBillingOperation',
      {
        data: {
          ...params,
          startedBy: identity?.fullName || 'Unknown User',
          jobId: job?.jobId ? Number(job.jobId) : null,
        },
      },
      {
        onSuccess: () => {
          setSubmittingStep(null);
          notify(`${params.step} operation triggered`, { type: 'info' });
          fetchActiveJob();
        },
        onError: (err: any) => {
          setSubmittingStep(null);
          notify(err.message, { type: 'error' });
        },
      }
    );
  };

  const handleConfirmAction = () => {
    const isStop = confirmMode.type === 'STOP';
    setActionLoading(true);

    execute(
      'ExecuteBillingOperation',
      {
        data: {
          step: isStop ? 'STOP_TASK' : 'CLOSE_JOB',
          stageId: isStop ? Number(confirmMode.stageId) : null,
          jobId: job?.jobId ? Number(job.jobId) : null,
          startedBy: identity?.fullName || 'System',
        },
      },
      {
        onSuccess: () => {
          setActionLoading(false);
          notify(isStop ? 'Task stopped' : 'Billing cycle closed/archived', { type: 'info' });
          setActionConfirmOpen(false);
          fetchActiveJob();
        },
        onError: (err: any) => {
          setActionLoading(false);
          notify(err.message, { type: 'error' });
        },
      }
    );
  };
  if (initialLoading) return <Loading />;

  const getStepStats = (step: string) => {
    if (!job || !job.stages) return { latest: null, allAttempts: [], zipAttempts: [] };

    const attempts = job.stages.filter((s: any) => s.step === step).sort((a: any, b: any) => b.stageId - a.stageId);

    const zipAttempts =
      step === 'SYNC'
        ? job.stages.filter((s: any) => s.step === 'ZIP_INVOICES').sort((a: any, b: any) => b.stageId - a.stageId)
        : [];

    return { latest: attempts[0], allAttempts: attempts, zipAttempts };
  };

  const formatTime = (dateStr: any) => {
    if (!dateStr) return 'Not Started';

    const numericTimestamp = Number(dateStr);

    const date = new Date(!isNaN(numericTimestamp) ? numericTimestamp : dateStr);

    if (isNaN(date.getTime())) {
      return 'Pending...';
    }

    return date.toLocaleString(undefined, {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };
  const getStatusChip = (status: string) => {
    const normalizedStatus = (status || '').toUpperCase();

    switch (normalizedStatus) {
      case 'COMPLETED':
      case 'DONE':
        return <Chip label="Done" color="primary" size="small" variant="filled" />;
      case 'PROCESSING':
      case 'RUNNING':
        return (
          <Chip icon={<CircularProgress size={12} color="inherit" />} label="Running" color="primary" size="small" />
        );
      case 'PARTIAL_SUCCESS':
        return <Chip label="Partial" color="warning" size="small" />;
      case 'ERROR':
      case 'FAILED':
      case 'STALE_FAILED':
        return <Chip label="Failed" color="error" size="small" variant="filled" />;
      case 'READY':
      case 'PENDING':
        return <Chip label="Ready" variant="outlined" size="small" />;
      default:
        return <Chip label={status || 'Unknown'} variant="outlined" size="small" />;
    }
  };

  return (
    <Box sx={{ p: 4, minHeight: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          {job?.jobId ? (
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h5" fontWeight="800" color="text.primary">
                Active Billing Process
              </Typography>

              <Chip
                label={`ID: #${job.jobId}`}
                size="small"
                variant="outlined"
                sx={{ borderStyle: 'dashed', color: 'text.secondary', fontWeight: 'bold' }}
              />
            </Box>
          ) : (
            <Typography variant="subtitle1" fontWeight="bold">
              No Active Job - Start Step 1 to begin a new cycle
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          size="medium"
          onClick={() => {
            setConfirmMode({ type: 'CLOSE' });
            setActionConfirmOpen(true);
          }}
          sx={{
            variant: 'outlined',
            color: 'error',
            borderRadius: 2,
            textTransform: 'none',
          }}
        >
          Close & Archive Cycle
        </Button>
      </Box>

      <Grid container spacing={2}>
        {taskConfig.map((config) => {
          const { latest, allAttempts, zipAttempts } = getStepStats(config.step);
          const isProcessing = latest?.status === 'PROCESSING';

          const moveStepStats = getStepStats('MOVE');
          const isMoveDone = moveStepStats.allAttempts.some((s: any) =>
            ['COMPLETED', 'PARTIAL_SUCCESS', 'STOPPED'].includes(s.status)
          );

          let canStart = false;

          const isBackupStep = config.step === 'BACKUP';

          if (!job) {
            canStart = config.step === 'MOVE';
          } else {
            const isMoveStep = config.step === 'MOVE';
            const dependencyMet = isMoveStep || isMoveDone;

            canStart = (!isAnythingRunning && dependencyMet) || isProcessing;
          }
          const cleanupDisabled = !canStart || !isMoveDone;
          return (
            <Grid item xs={12} md={6} lg={3} key={config.step}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" fontWeight="800">
                      {config.title}
                    </Typography>
                    {getStatusChip(latest?.status || 'Pending')}
                  </Box>

                  <Typography variant="body2" color="textSecondary" mb={2}>
                    {config.desc}
                  </Typography>
                  {/* <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      bgcolor: '#f5f5f5',
                      p: 0.5,
                      borderRadius: 1,
                      fontSize: '0.65rem',
                      color: 'text.secondary',
                      mb: 2,
                      borderLeft: '3px solid #ddd',
                    }}
                  >
                    ⏱️ {getBenchmarkNote(config.step)}
                  </Typography> */}
                  <Typography
                    variant="overline"
                    color="textSecondary"
                    sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}
                  >
                    Attempt History ({allAttempts.length})
                  </Typography>

                  <Box
                    sx={{
                      height: 220,
                      overflowY: 'auto',
                      pr: 1,
                      mb: 2,
                      '&::-webkit-scrollbar': { width: '6px' },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '10px' },
                    }}
                  >
                    {allAttempts.length === 0 ? (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px dashed #ccc',
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          No attempts yet
                        </Typography>
                      </Box>
                    ) : (
                      allAttempts.map((attempt: any, index: number) => {
                        const attemptNum = allAttempts.length - index;

                        const isCleanup = config.step === 'CLEANUP';

                        const isCurrent = index === 0;
                        const isThisAttemptRunning = attempt.status === 'PROCESSING' || attempt.status === 'RUNNING';
                        let targetDir = '';
                        let attemptBackupFolder = '';
                        if (config.step === 'BACKUP' && attempt.filters) {
                          try {
                            const parsed =
                              typeof attempt.filters === 'string' ? JSON.parse(attempt.filters) : attempt.filters;
                            attemptBackupFolder = parsed.backupFolderName;
                          } catch (e) {
                            attemptBackupFolder = 'Unknown Folder';
                          }
                        }
                        if (isCleanup && attempt.filters) {
                          try {
                            const parsed =
                              typeof attempt.filters === 'string' ? JSON.parse(attempt.filters) : attempt.filters;
                            targetDir = parsed.target_directory?.includes('Sync') ? 'FTP Sync' : 'Billing Reports';
                          } catch (e) {
                            targetDir = 'Unknown Directory';
                          }
                        }
                        return (
                          <Box
                            key={attempt.stageId}
                            sx={{
                              p: 1.5,
                              mb: 1.5,
                              borderRadius: 2,
                              bgcolor: isCurrent ? '#f0f7ff' : '#fff',
                              border: isCurrent ? '1px solid #007bff' : '1px solid #eee',
                              boxShadow: isCurrent ? '0 2px 8px rgba(0,123,255,0.1)' : 'none',
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                              <Box display="flex" flexDirection="column">
                                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                  Attempt #{attemptNum} {isCurrent && '(Latest)'}
                                </Typography>

                                {config.step === 'BACKUP' && attemptBackupFolder && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', fontSize: '0.65rem', fontFamily: 'monospace' }}
                                  >
                                    Folder: {attemptBackupFolder}
                                  </Typography>
                                )}
                              </Box>

                              <Box display="flex" alignItems="center" gap={1}>
                                {getStatusChip(attempt.status)}
                                {isThisAttemptRunning && (
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    sx={{ minWidth: 0, px: 1, py: 0, height: 24, fontSize: '0.6rem' }}
                                    onClick={() => {
                                      setConfirmMode({ type: 'STOP', stageId: attempt.stageId });
                                      setActionConfirmOpen(true);
                                    }}
                                  >
                                    STOP
                                  </Button>
                                )}
                              </Box>
                            </Box>
                            {isCleanup && (
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', color: 'primary.main', fontWeight: 'bold', mb: 1 }}
                              >
                                {targetDir}
                              </Typography>
                            )}
                            {!isCleanup ? (
                              <LinearProgress
                                variant={isThisAttemptRunning ? 'indeterminate' : 'determinate'}
                                value={attempt.totalFiles > 0 ? (attempt.successCount / attempt.totalFiles) * 100 : 0}
                                sx={{ height: 4, borderRadius: 2, mb: 1 }}
                              />
                            ) : isThisAttemptRunning ? (
                              <LinearProgress
                                variant="indeterminate"
                                sx={{ height: 2, borderRadius: 2, mb: 1, opacity: 0.6 }}
                              />
                            ) : null}
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary">
                                {isCleanup
                                  ? `Total Deleted: ${attempt.successCount} files`
                                  : `${attempt.successCount} / ${attempt.totalFiles} success`}
                              </Typography>
                              {attempt.errorCount > 0 && (
                                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                  {attempt.errorCount} failed
                                </Typography>
                              )}
                            </Box>
                            {attempt.errorCount > 0 && (
                              <Button
                                size="small"
                                color="error"
                                variant="text"
                                sx={{ fontSize: '0.65rem', mt: 0.5, p: 0, textTransform: 'none', minWidth: 0 }}
                                onClick={() => handleViewIssues(attempt.stageId)}
                              >
                                View {attempt.errorCount} Issues
                              </Button>
                            )}
                            <Typography
                              variant="caption"
                              sx={{ fontSize: '0.65rem', color: 'text.disabled', display: 'block', mt: 0.5 }}
                            >
                              Started: {formatTime(attempt.startTime)}
                            </Typography>
                          </Box>
                        );
                      })
                    )}
                  </Box>

                  {isBackupStep && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.2,
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                          TARGET FOLDER:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', color: activeFolderName ? 'primary.main' : 'orange' }}
                        >
                          {activeFolderName || 'No date available. Please select one.'}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setHasConfigured(true);
                          setIsDialogOpen(true);
                        }}
                      >
                        {activeFolderName ? 'Edit' : 'Select'}
                      </Button>
                    </Box>
                  )}
                  <Divider sx={{ mb: 2, mt: 'auto' }} />

                  {config.step === 'CLEANUP' ? (
                    <Box display="flex" gap={1}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        disabled={!canStart || !isMoveDone}
                        startIcon={<DeleteForeverIcon />}
                        onClick={() => handleStartClick({ step: 'CLEANUP', actionType: 'TEMP_FOLDER' })}
                        sx={{ fontSize: '0.7rem', py: 1 }}
                      >
                        Clear Staging
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        disabled={!canStart || !isMoveDone}
                        startIcon={<DeleteForeverIcon />}
                        onClick={() => handleStartClick({ step: 'CLEANUP', actionType: 'FTP_SYNC' })}
                        sx={{ fontSize: '0.7rem', py: 1 }}
                      >
                        Clear FTP
                      </Button>
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={1}>
                      {config.step === 'SYNC' && (
                        <Box
                          sx={{
                            mb: 2,
                            p: 1.2,
                            borderRadius: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: '1px solid #e0e0e0',
                            bgcolor: '#fafafa',
                          }}
                        >
                          <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              TPA BUNDLING:
                            </Typography>
                            <Typography variant="body2" color="primary">
                              {zipAttempts.some((a: any) => a.status === 'COMPLETED')
                                ? 'Ready for Sync'
                                : 'Preparation Required'}
                            </Typography>
                          </Box>
                          <Button size="small" startIcon={<EditIcon />} onClick={() => setZipDialogOpen(true)}>
                            Manage
                          </Button>
                        </Box>
                      )}
                      <Button
                        fullWidth
                        disabled={!canStart || (isBackupStep && !activeFolderName) || checkingFtp || isTriggering}
                        variant={latest?.status === 'COMPLETED' ? 'outlined' : 'contained'}
                        startIcon={
                          submittingStep === config.step ||
                          (checkingFtp && config.step === 'SYNC') ? null : config.step === 'CLEANUP' ? (
                            <DeleteForeverIcon />
                          ) : (
                            <PlayArrowIcon />
                          )
                        }
                        onClick={() => {
                          const payload: any = { step: config.step };
                          if (isBackupStep) payload.backupFolderName = activeFolderName;
                          handleStartClick(payload);
                        }}
                        sx={{
                          opacity: canStart ? 1 : 0.5,
                          cursor: canStart ? 'pointer' : 'not-allowed',
                          bgcolor: latest?.status === 'COMPLETED' ? 'transparent' : 'primary',
                          color: latest?.status === 'COMPLETED' ? 'primary' : '#fff',
                          borderColor: 'primary',
                          '&:hover': {
                            bgcolor: latest?.status === 'COMPLETED' ? '#f0fbfb' : '#006666',
                            borderColor: 'primary',
                          },
                        }}
                      >
                        {submittingStep === config.step ? (
                          <Box display="flex" alignItems="center">
                            <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
                            Triggering...
                          </Box>
                        ) : checkingFtp && config.step === 'SYNC' ? (
                          <Box display="flex" alignItems="center">
                            <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
                            Checking FTP...
                          </Box>
                        ) : isProcessing ? (
                          'Running (Force Restart)'
                        ) : !job && config.step === 'MOVE' ? (
                          'Start New Billing Job'
                        ) : !canStart ? (
                          'Wait for current step...'
                        ) : latest?.status === 'STOPPED' || latest?.status === 'COMPLETED' ? (
                          'Re-Run Step'
                        ) : (
                          'Start Step'
                        )}
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 'bold' }}>Backup Configuration</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Select the billing cycle period to auto-generate the folder name.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Week</InputLabel>
                <Select
                  value={selWeek}
                  label="Select Week"
                  onChange={(e) => {
                    setSelWeek(Number(e.target.value));
                    setCustomFolderName('');
                  }}
                >
                  {[1, 2, 3, 4].map((w) => (
                    <MenuItem key={w} value={w}>
                      Week {w}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Month</InputLabel>
                <Select
                  value={selMonth}
                  label="Select Month"
                  onChange={(e) => {
                    setSelMonth(Number(e.target.value));
                    setCustomFolderName('');
                  }}
                >
                  {months.map((m, i) => (
                    <MenuItem key={m} value={i}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Year</InputLabel>
                <Select
                  value={selYear}
                  label="Select Year"
                  onChange={(e) => {
                    setSelYear(Number(e.target.value));
                    setCustomFolderName('');
                  }}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          <TextField
            label="Final Folder Name"
            fullWidth
            variant="standard"
            value={activeFolderName}
            onChange={(e) => setCustomFolderName(e.target.value)}
            helperText="You can manually edit the generated name above if needed"
            InputProps={{
              style: { fontFamily: 'monospace', fontSize: '1.1rem', color: '#007bff' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => setIsDialogOpen(false)} variant="contained" color="primary">
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Folder Already Exists</DialogTitle>
        <DialogContent>
          <Typography>
            The backup folder <strong>{activeFolderName}</strong> already exists in Azure.
          </Typography>
          <Typography sx={{ mt: 2, fontSize: '0.9rem', color: 'text.secondary' }}>
            Starting this task will overwrite or add files to the existing folder. Do you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleStartTask(pendingParams);
              setConfirmDialogOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            Yes, Proceed
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ftpConfirmOpen} onClose={() => setFtpConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>FTP Folders Not Empty</DialogTitle>
        <DialogContent>
          <Typography>One or more client FTP folders already contain files.</Typography>
          <Typography sx={{ mt: 2, fontSize: '0.9rem', color: 'text.secondary' }}>
            Syncing now will add new files alongside existing ones. We recommend cleaning the FTP area first if this is
            a fresh cycle.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFtpConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleStartTask(pendingParams);
              setFtpConfirmOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            Sync Anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={issuesDialogOpen} onClose={() => setIssuesDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 'bold' }}>Sync Issues / Failed Files</DialogTitle>
        <DialogContent dividers>
          {loadingIssues ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : selectedStageIssues.length === 0 ? (
            <Typography>No specific file errors found.</Typography>
          ) : (
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                  <th style={{ padding: '8px' }}>File Name</th>
                  <th style={{ padding: '8px' }}>Status</th>
                  <th style={{ padding: '8px' }}>Error Message</th>
                </tr>
              </thead>
              <tbody>
                {selectedStageIssues.map((issue, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px', fontSize: '0.85rem' }}>{issue.file_name}</td>
                    <td style={{ padding: '8px' }}>
                      <Chip label={issue.status} size="small" color="error" variant="outlined" />
                    </td>
                    <td style={{ padding: '8px', fontSize: '0.8rem', color: '#666' }}>
                      {issue.error_message || 'No specific error message provided.'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssuesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={actionConfirmOpen} onClose={() => setActionConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {confirmMode.type === 'STOP' ? 'Stop Active Task?' : 'Close Billing Cycle?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmMode.type === 'STOP'
              ? 'Are you sure you want to stop this specific task?'
              : 'This will archive the current cycle. All steps will be hidden from the active dashboard. You must start Step 1 to begin a new cycle.'}
          </Typography>
          {confirmMode.type === 'STOP' && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff4e5', borderRadius: 1, borderLeft: '4px solid #ffa726' }}>
              <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 'bold', display: 'block' }}>
                NOTE:
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Due to high-speed background processing, the file count may increase slightly for a few seconds after
                stopping as current transfers finish safely.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setActionConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            disabled={actionLoading}
            color={confirmMode.type === 'STOP' ? 'error' : 'primary'}
            startIcon={actionLoading && <CircularProgress size={16} color="inherit" />}
          >
            {actionLoading ? 'Processing...' : `Confirm ${confirmMode.type === 'STOP' ? 'Stop' : 'Archive'}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={zipDialogOpen} onClose={() => setZipDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 'bold' }}>TPA Invoice Bundling (ZIP)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Before syncing to FTP, individual TPA invoices must be bundled into ZIP files. You can track previous
            attempts and trigger a new bundling cycle here.
          </Typography>

          <Typography variant="overline" sx={{ fontWeight: 'bold' }}>
            Recent Bundling Attempts
          </Typography>
          <Box
            sx={{
              mt: 1,
              maxHeight: 200,
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: 2,
              p: 2,
              bgcolor: '#fdfdfd',
            }}
          >
            {zipAttempts.length === 0 ? (
              <Typography variant="caption" color="textDisabled">
                No history found for this job.
              </Typography>
            ) : (
              zipAttempts.map((attempt: any, index: number) => (
                <Box
                  key={attempt.stageId}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                    pb: 1,
                    borderBottom: index !== zipAttempts.length - 1 ? '1px solid #f0f0f0' : 'none',
                  }}
                >
                  <Box>
                    <Typography variant="caption" fontWeight="bold" display="block">
                      Attempt #{zipAttempts.length - index}
                    </Typography>
                    <Typography variant="caption" color="textDisabled">
                      {formatTime(attempt.startTime)}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="caption" fontWeight="bold">
                      {attempt.successCount} / {attempt.totalFiles}
                    </Typography>
                    {getStatusChip(attempt.status)}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setZipDialogOpen(false)} color="inherit">
            Close
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={
              submittingStep === 'ZIP_INVOICES' ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />
            }
            disabled={isAnythingRunning || submittingStep === 'ZIP_INVOICES'}
            onClick={() => handleStartTask({ step: 'ZIP_INVOICES' })}
          >
            {submittingStep === 'ZIP_INVOICES' ? 'Bundling...' : 'Start Bundling'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
